import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import * as XLSX from 'xlsx';

const RENKLER = ['#e07b00', '#3b82f6', '#22c55e', '#a855f7', '#ef4444', '#eab308'];

export default function Raporlar() {
  const { profil } = useAuth();
  const [loading, setLoading] = useState(true);
  const [aktifTab, setAktifTab] = useState('genel');

  const bugun = new Date();
  const ayBas = new Date(bugun.getFullYear(), bugun.getMonth(), 1).toISOString().split('T')[0];
  const [tarihBas, setTarihBas] = useState(ayBas);
  const [tarihBit, setTarihBit] = useState(bugun.toISOString().split('T')[0]);

  const [stats, setStats] = useState({ toplamGelir: 0, toplamKomisyon: 0, toplamKar: 0, toplamSiparis: 0, odenenTutar: 0, odenmemisTutar: 0, tahsilatOrani: 0 });
  const [firmaData, setFirmaData] = useState([]);
  const [gunlukData, setGunlukData] = useState([]);
  const [urunData, setUrunData] = useState([]);
  const [temsilciData, setTemsilciData] = useState([]);
  const [faturaData, setFaturaData] = useState([]);
  const [siparisler, setSiparisler] = useState([]);

  useEffect(() => { fetchData(); }, [tarihBas, tarihBit]);

  function hizliSecim(tip) {
    const bugun = new Date();
    if (tip === 'bugun') {
      const b = bugun.toISOString().split('T')[0];
      setTarihBas(b); setTarihBit(b);
    } else if (tip === 'bu_ay') {
      setTarihBas(new Date(bugun.getFullYear(), bugun.getMonth(), 1).toISOString().split('T')[0]);
      setTarihBit(bugun.toISOString().split('T')[0]);
    } else if (tip === 'gecen_ay') {
      setTarihBas(new Date(bugun.getFullYear(), bugun.getMonth() - 1, 1).toISOString().split('T')[0]);
      setTarihBit(new Date(bugun.getFullYear(), bugun.getMonth(), 0).toISOString().split('T')[0]);
    } else if (tip === 'bu_yil') {
      setTarihBas(`${bugun.getFullYear()}-01-01`);
      setTarihBit(bugun.toISOString().split('T')[0]);
    } else if (tip === 'son_30') {
      const b = new Date(bugun); b.setDate(b.getDate() - 30);
      setTarihBas(b.toISOString().split('T')[0]);
      setTarihBit(bugun.toISOString().split('T')[0]);
    } else if (tip === 'son_90') {
      const b = new Date(bugun); b.setDate(b.getDate() - 90);
      setTarihBas(b.toISOString().split('T')[0]);
      setTarihBit(bugun.toISOString().split('T')[0]);
    }
  }

  async function fetchData() {
    setLoading(true);
    let q = supabase.from('siparisler')
      .select('*, firmalar(ad), kullanicilar(ad, soyad), siparis_kalemleri(miktar, toplam, urunler(ad, alt_grup, firmalar(ad)))')
      .gte('created_at', tarihBas)
      .lte('created_at', tarihBit + 'T23:59:59')
      .not('siparis_durumu', 'eq', 'iptal');

    if (profil?.rol === 'temsilci') q = q.eq('temsilci_id', profil.id);
    const { data } = await q;
    const s = data || [];
    setSiparisler(s);

    const toplamGelir = s.reduce((a, r) => a + (r.toplam_tutar || 0), 0);
    const toplamKomisyon = s.reduce((a, r) => a + (r.toplam_komisyon || 0), 0);
    const toplamKar = s.reduce((a, r) => a + (r.net_kar || 0), 0);
    const odenenTutar = s.filter(r => r.odeme_durumu === 'odendi').reduce((a, r) => a + (r.toplam_tutar || 0), 0);
    const odenmemisTutar = s.filter(r => r.odeme_durumu !== 'odendi').reduce((a, r) => a + (r.toplam_tutar || 0), 0);
    const tahsilatOrani = toplamGelir > 0 ? ((odenenTutar / toplamGelir) * 100).toFixed(1) : 0;
    setStats({ toplamGelir, toplamKomisyon, toplamKar, toplamSiparis: s.length, odenenTutar, odenmemisTutar, tahsilatOrani });

    // Firma bazlı
    const firmaMap = {};
    s.forEach(r => {
      const ad = r.firmalar?.ad || 'Diğer';
      if (!firmaMap[ad]) firmaMap[ad] = { ad, gelir: 0, komisyon: 0, kar: 0, adet: 0, odenen: 0 };
      firmaMap[ad].gelir += r.toplam_tutar || 0;
      firmaMap[ad].komisyon += r.toplam_komisyon || 0;
      firmaMap[ad].kar += r.net_kar || 0;
      firmaMap[ad].adet++;
      if (r.odeme_durumu === 'odendi') firmaMap[ad].odenen += r.toplam_tutar || 0;
    });
    setFirmaData(Object.values(firmaMap));

    // Günlük
    const gunMap = {};
    s.forEach(r => {
      const gun = r.created_at.split('T')[0];
      if (!gunMap[gun]) gunMap[gun] = { tarih: gun, gelir: 0, adet: 0 };
      gunMap[gun].gelir += r.toplam_tutar || 0;
      gunMap[gun].adet++;
    });
    setGunlukData(Object.values(gunMap).sort((a, b) => a.tarih.localeCompare(b.tarih)));

    // Ürün bazlı
    const urunMap = {};
    s.forEach(r => {
      (r.siparis_kalemleri || []).forEach(k => {
        const ad = k.urunler?.ad || 'Bilinmiyor';
        if (!urunMap[ad]) urunMap[ad] = { ad, adet: 0, tutar: 0, firma: k.urunler?.firmalar?.ad || '-', altGrup: k.urunler?.alt_grup || '' };
        urunMap[ad].adet += k.miktar || 0;
        urunMap[ad].tutar += k.toplam || 0;
      });
    });
    setUrunData(Object.values(urunMap).sort((a, b) => b.adet - a.adet));

    // Temsilci bazlı
    const temsilciMap = {};
    s.forEach(r => {
      const ad = r.kullanicilar ? `${r.kullanicilar.ad} ${r.kullanicilar.soyad}` : 'Bilinmiyor';
      if (!temsilciMap[ad]) temsilciMap[ad] = { ad, gelir: 0, komisyon: 0, adet: 0, odenen: 0, odenmemis: 0 };
      temsilciMap[ad].gelir += r.toplam_tutar || 0;
      temsilciMap[ad].komisyon += r.toplam_komisyon || 0;
      temsilciMap[ad].adet++;
      if (r.odeme_durumu === 'odendi') temsilciMap[ad].odenen += r.toplam_tutar || 0;
      else temsilciMap[ad].odenmemis += r.toplam_tutar || 0;
    });
    setTemsilciData(Object.values(temsilciMap).sort((a, b) => b.gelir - a.gelir));

    // Fatura dağılımı
    const faturali = s.filter(r => r.fatura_durumu).reduce((a, r) => a + (r.toplam_tutar || 0), 0);
    const faturasiz = s.filter(r => !r.fatura_durumu).reduce((a, r) => a + (r.toplam_tutar || 0), 0);
    setFaturaData([
      { ad: 'Faturalı', tutar: faturali },
      { ad: 'Faturasız', tutar: faturasiz },
    ]);

    setLoading(false);
  }

  function excelExport() {
    const rows = siparisler.map(s => ({
      'Sipariş No': s.siparis_no,
      'Firma': s.firmalar?.ad,
      'Temsilci': s.kullanicilar ? `${s.kullanicilar.ad} ${s.kullanicilar.soyad}` : '',
      'Tutar': s.toplam_tutar,
      'Komisyon': s.toplam_komisyon,
      'Maliyet': s.toplam_maliyet,
      'Net Kar': s.net_kar,
      'Ödeme': s.odeme_durumu,
      'Fatura': s.fatura_durumu ? 'Kesildi' : 'Kesilmedi',
      'Tarih': new Date(s.created_at).toLocaleDateString('tr-TR'),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rapor');
    XLSX.writeFile(wb, `rapor_${tarihBas}_${tarihBit}.xlsx`);
  }

  const fmt = v => v.toLocaleString('tr-TR');

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Raporlama</h1>
          <p className="page-subtitle">{tarihBas} — {tarihBit}</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={excelExport}>📊 Excel</button>
      </div>

      {/* HIZ BUTONLAR */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['bugun','Bugün'],['bu_ay','Bu Ay'],['gecen_ay','Geçen Ay'],['son_30','Son 30 Gün'],['son_90','Son 90 Gün'],['bu_yil','Bu Yıl']].map(([k, v]) => (
          <button key={k} className="btn btn-ghost btn-sm" onClick={() => hizliSecim(k)}>{v}</button>
        ))}
        <input type="date" className="input" value={tarihBas} onChange={e => setTarihBas(e.target.value)} style={{ width: 150 }} />
        <input type="date" className="input" value={tarihBit} onChange={e => setTarihBit(e.target.value)} style={{ width: 150 }} />
      </div>

      {loading ? <div className="loading">Yükleniyor...</div> : (
        <>
          {/* KPI KARTLARI */}
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 20 }}>
            <div className="stat-card">
              <div className="stat-label">Toplam Sipariş</div>
              <div className="stat-value">{stats.toplamSiparis}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Toplam Satış</div>
              <div className="stat-value" style={{ fontSize: 18, color: 'var(--green)' }}>{fmt(stats.toplamGelir)} ₺</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Tahsilat Oranı</div>
              <div className="stat-value" style={{ fontSize: 18, color: stats.tahsilatOrani >= 70 ? 'var(--green)' : 'var(--red)' }}>%{stats.tahsilatOrani}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Alınan Ödemeler</div>
              <div className="stat-value" style={{ fontSize: 18, color: 'var(--green)' }}>{fmt(stats.odenenTutar)} ₺</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Alınmayan Ödemeler</div>
              <div className="stat-value" style={{ fontSize: 18, color: 'var(--red)' }}>{fmt(stats.odenmemisTutar)} ₺</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Toplam Komisyon</div>
              <div className="stat-value" style={{ fontSize: 18, color: 'var(--yellow)' }}>{fmt(stats.toplamKomisyon)} ₺</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Net Kar</div>
              <div className="stat-value" style={{ fontSize: 18, color: 'var(--accent)' }}>{fmt(stats.toplamKar)} ₺</div>
            </div>
          </div>

          {/* SEKMELER */}
          <div className="tabs">
            <button className={`tab-btn ${aktifTab === 'genel' ? 'active' : ''}`} onClick={() => setAktifTab('genel')}>📊 Genel</button>
            <button className={`tab-btn ${aktifTab === 'firma' ? 'active' : ''}`} onClick={() => setAktifTab('firma')}>🏢 Marka Bazlı</button>
            <button className={`tab-btn ${aktifTab === 'temsilci' ? 'active' : ''}`} onClick={() => setAktifTab('temsilci')}>👤 Temsilci</button>
            <button className={`tab-btn ${aktifTab === 'urun' ? 'active' : ''}`} onClick={() => setAktifTab('urun')}>📦 Ürün</button>
            <button className={`tab-btn ${aktifTab === 'fatura' ? 'active' : ''}`} onClick={() => setAktifTab('fatura')}>🧾 Fatura</button>
          </div>

          {/* GENEL */}
          {aktifTab === 'genel' && (
            <>
              {gunlukData.length > 0 && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3 style={{ marginBottom: 16, fontWeight: 700 }}>Günlük Satış</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={gunlukData}>
                      <XAxis dataKey="tarih" tick={{ fill: '#8b92a8', fontSize: 10 }} />
                      <YAxis tick={{ fill: '#8b92a8', fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                      <Tooltip formatter={v => `${fmt(v)} ₺`} contentStyle={{ background: '#ffffff', border: '1px solid #e0e3ea', borderRadius: 8 }} />
                      <Bar dataKey="gelir" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Gelir" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}

          {/* MARKA BAZLI */}
          {aktifTab === 'firma' && (
            <>
              {firmaData.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                  <div className="card">
                    <h3 style={{ marginBottom: 16, fontWeight: 700 }}>Marka Gelir Dağılımı</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={firmaData}>
                        <XAxis dataKey="ad" tick={{ fill: '#8b92a8', fontSize: 11 }} />
                        <YAxis tick={{ fill: '#8b92a8', fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                        <Tooltip formatter={v => `${fmt(v)} ₺`} contentStyle={{ background: '#ffffff', border: '1px solid #e0e3ea', borderRadius: 8 }} />
                        <Bar dataKey="gelir" fill="#e07b00" radius={[4, 4, 0, 0]} name="Gelir" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="card">
                    <h3 style={{ marginBottom: 16, fontWeight: 700 }}>Marka Kar Dağılımı</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={firmaData} dataKey="kar" nameKey="ad" cx="50%" cy="50%" outerRadius={80} label={({ ad, percent }) => `${ad} %${(percent * 100).toFixed(0)}`}>
                          {firmaData.map((_, i) => <Cell key={i} fill={RENKLER[i % RENKLER.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => `${fmt(v)} ₺`} contentStyle={{ background: '#ffffff', border: '1px solid #e0e3ea', borderRadius: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              <div className="card" style={{ padding: 0 }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700 }}>Marka Kar-Zarar Tablosu</div>
                <table className="table">
                  <thead><tr><th>Marka</th><th>Sipariş</th><th>Gelir</th><th>Komisyon</th><th>Net Kar</th><th>Tahsilat</th><th>Tahsilat %</th></tr></thead>
                  <tbody>
                    {firmaData.map((f, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{f.ad}</td>
                        <td>{f.adet}</td>
                        <td style={{ color: 'var(--green)' }}>{fmt(f.gelir)} ₺</td>
                        <td style={{ color: 'var(--yellow)' }}>{fmt(f.komisyon)} ₺</td>
                        <td style={{ color: 'var(--accent)', fontWeight: 600 }}>{fmt(f.kar)} ₺</td>
                        <td style={{ color: 'var(--blue)' }}>{fmt(f.odenen)} ₺</td>
                        <td>
                          <span className={`badge ${f.gelir > 0 && f.odenen/f.gelir >= 0.7 ? 'badge-green' : 'badge-red'}`}>
                            %{f.gelir > 0 ? ((f.odenen/f.gelir)*100).toFixed(0) : 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* TEMSİLCİ */}
          {aktifTab === 'temsilci' && (
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700 }}>Temsilci Performans Raporu</div>
              <table className="table">
                <thead>
                  <tr>
                    <th>Temsilci</th>
                    <th>Sipariş</th>
                    <th>Toplam Satış</th>
                    <th>Alınan Ödeme</th>
                    <th>Alınmayan Ödeme</th>
                    <th>Komisyon</th>
                    <th>Tahsilat %</th>
                    <th>Sıra</th>
                  </tr>
                </thead>
                <tbody>
                  {temsilciData.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--text2)' }}>Veri yok</td></tr>
                  ) : temsilciData.map((t, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{t.ad}</td>
                      <td>{t.adet}</td>
                      <td style={{ color: 'var(--green)', fontWeight: 600 }}>{fmt(t.gelir)} ₺</td>
                      <td style={{ color: 'var(--green)' }}>{fmt(t.odenen)} ₺</td>
                      <td style={{ color: 'var(--red)' }}>{fmt(t.odenmemis)} ₺</td>
                      <td style={{ color: 'var(--yellow)' }}>{fmt(t.komisyon)} ₺</td>
                      <td>
                        <span className={`badge ${t.gelir > 0 && t.odenen / t.gelir >= 0.7 ? 'badge-green' : 'badge-red'}`}>
                          %{t.gelir > 0 ? ((t.odenen / t.gelir) * 100).toFixed(0) : 0}
                        </span>
                      </td>
                      <td><span className="badge badge-orange">#{i + 1}</span></td>
                    </tr>
                  ))}
                </tbody>
                {temsilciData.length > 0 && (
                  <tfoot>
                    <tr style={{ background: 'var(--bg3)', fontWeight: 700 }}>
                      <td>TOPLAM</td>
                      <td>{temsilciData.reduce((a, t) => a + t.adet, 0)}</td>
                      <td style={{ color: 'var(--green)' }}>{fmt(temsilciData.reduce((a, t) => a + t.gelir, 0))} ₺</td>
                      <td style={{ color: 'var(--green)' }}>{fmt(temsilciData.reduce((a, t) => a + t.odenen, 0))} ₺</td>
                      <td style={{ color: 'var(--red)' }}>{fmt(temsilciData.reduce((a, t) => a + t.odenmemis, 0))} ₺</td>
                      <td style={{ color: 'var(--yellow)' }}>{fmt(temsilciData.reduce((a, t) => a + t.komisyon, 0))} ₺</td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* ÜRÜN */}
          {aktifTab === 'urun' && (
            <UrunRaporu urunData={urunData} fmt={fmt} />
          )}

          {/* FATURA */}
          {aktifTab === 'fatura' && (
            <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20 }}>
              <div className="card">
                <h3 style={{ marginBottom: 16, fontWeight: 700 }}>Fatura Dağılımı</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={faturaData} dataKey="tutar" nameKey="ad" cx="50%" cy="50%" outerRadius={80} label={({ ad, percent }) => `${ad} %${(percent*100).toFixed(0)}`}>
                      <Cell fill="#22c55e" />
                      <Cell fill="#ef4444" />
                    </Pie>
                    <Tooltip formatter={v => `${fmt(v)} ₺`} contentStyle={{ background: '#ffffff', border: '1px solid #e0e3ea', borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ color: 'var(--text2)' }}>Faturalı</span>
                    <span style={{ color: 'var(--green)', fontWeight: 600 }}>{fmt(faturaData[0]?.tutar || 0)} ₺</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                    <span style={{ color: 'var(--text2)' }}>Faturasız</span>
                    <span style={{ color: 'var(--red)', fontWeight: 600 }}>{fmt(faturaData[1]?.tutar || 0)} ₺</span>
                  </div>
                </div>
              </div>
              <div className="card" style={{ padding: 0 }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700 }}>Faturasız Siparişler</div>
                <table className="table">
                  <thead><tr><th>Sipariş No</th><th>Firma</th><th>Tutar</th><th>Ödeme</th><th>Tarih</th></tr></thead>
                  <tbody>
                    {siparisler.filter(s => !s.fatura_durumu).map(s => (
                      <tr key={s.id}>
                        <td><span className="mono" style={{ color: 'var(--accent)' }}>{s.siparis_no}</span></td>
                        <td style={{ color: 'var(--text2)' }}>{s.firmalar?.ad}</td>
                        <td style={{ fontWeight: 600 }}>{fmt(s.toplam_tutar || 0)} ₺</td>
                        <td><span className={`badge ${s.odeme_durumu === 'odendi' ? 'badge-green' : 'badge-red'}`}>{s.odeme_durumu}</span></td>
                        <td style={{ fontSize: 12, color: 'var(--text2)' }}>{new Date(s.created_at).toLocaleDateString('tr-TR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function UrunRaporu({ urunData, fmt }) {
  const [arama, setArama] = useState('');
  const [filtreFirma, setFiltreFirma] = useState('');
  const [filtreGrup, setFiltreGrup] = useState('');

  const firmalar = [...new Set(urunData.map(u => u.firma).filter(Boolean))].sort();
  const gruplar = [...new Set(
    urunData
      .filter(u => !filtreFirma || u.firma === filtreFirma)
      .map(u => u.altGrup || '').filter(Boolean)
  )].sort();

  const filtreli = urunData.filter(u => {
    if (arama && !u.ad.toLowerCase().includes(arama.toLowerCase())) return false;
    if (filtreFirma && u.firma !== filtreFirma) return false;
    if (filtreGrup && (u.altGrup || '') !== filtreGrup) return false;
    return true;
  });

  return (
    <div className="card" style={{ padding: 0 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontWeight: 700 }}>En Çok Satan Ürünler</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input className="input" placeholder="Ürün ara..." value={arama} onChange={e => setArama(e.target.value)} style={{ maxWidth: 180 }} />
          <select className="select" value={filtreFirma} onChange={e => { setFiltreFirma(e.target.value); setFiltreGrup(''); }} style={{ maxWidth: 160 }}>
            <option value="">Tüm Firmalar</option>
            {firmalar.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select className="select" value={filtreGrup} onChange={e => setFiltreGrup(e.target.value)} style={{ maxWidth: 160 }}>
            <option value="">Tüm Alt Gruplar</option>
            {gruplar.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          {(arama || filtreFirma || filtreGrup) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setArama(''); setFiltreFirma(''); setFiltreGrup(''); }}>Temizle</button>
          )}
        </div>
      </div>
      <table className="table">
        <thead><tr><th>Ürün</th><th>Firma</th><th>Alt Grup</th><th>Adet</th><th>Tutar</th></tr></thead>
        <tbody>
          {filtreli.length === 0 ? (
            <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: 'var(--text2)' }}>Veri yok</td></tr>
          ) : filtreli.map((u, i) => (
            <tr key={i}>
              <td style={{ fontWeight: 500 }}>{u.ad}</td>
              <td style={{ color: 'var(--text2)' }}>{u.firma}</td>
              <td style={{ color: 'var(--text2)', fontSize: 12 }}>{u.altGrup || '-'}</td>
              <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{u.adet}</td>
              <td style={{ color: 'var(--green)' }}>{fmt(u.tutar)} ₺</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
